import React, {useState} from "react";
import { Grid, IconButton, TextField } from "@material-ui/core";
import { MdClose } from "react-icons/md";
import { useFormikContext } from "formik";
import Autocomplete from "@material-ui/lab/Autocomplete";
import styled, { css } from "styled-components";

import SelectMultiLevel from "app/common/components/input/Menu/SelectMultiLevel";
import { isEmpty } from "lodash";
import DateInput from "app/common/components/input/Date";
import Util from "utils/Utils";

function CriteriaRow({
  lookupColumns = [],
  lookupListId,
  currentSheetColumns = [],
  currentListId,
  hiddenMultiLevels,
  value: {
    lhs,
    operatorType,
    operator,
    rhsType,
    rhs,
    condition,
    startDate,
    endDate,
    numberFrom,
    numberTo,
  },
  index,
  setFieldValue,
  criteriaService,
  isHoveredCriteriaRow = true,
  isColumnTypeDisabled,
  addFilterScroller,
  forDuplication = false
}) {
  const rhsValueOptions = criteriaService.getRhsValueOptions(isColumnTypeDisabled);
  const [lhsColumn, setLhsColumn] = React.useState(
    lookupColumns.find((col) => col.alias_path === lhs) || {}
  );
  const isRhsHidden =
    operator === "string_empty" ||
    operator === "string_not_empty" ||
    operator === "date_today" ||
    operator === "date_tomorrow" ||
    operator === "date_yesterday" ||
    operator === "date_l_s_d" ||
    operator === "date_this_month" ||
    operator === "date_this_quarter" ||
    operator === "date_this_year" ||
    operator === "date_this_past" ||
    operator === "date_this_future" ||
    operator === "date_this_range" ||
    operator === "number_between";

  const isDate = operator === "date_e" ||
                  operator === "date_ne" ||
                  operator === "date_ge" ||
                  operator === "date_le" ||
                  operator === "date_g" ||
                  operator === "date_l"; 

  const isDateRange = operator === "date_this_range";
  const isNumberBetween = operator === "number_between";
  const operators = criteriaService.getOperators(operatorType,lhs);

  React.useEffect(() => {
    if (lhs && (!operator || !operators.find((i) => i.operator === operator))) {
      setFieldValue(`rows[${index}].operator`, operators[0].operator);
      if (
        operators[0].operator === "string_not_equal" ||
        operators[0].operator === "string_equal"
      ) {
        setFieldValue(`rows[${index}].rhs`, []);
      }
    }
  }, [index, lhs, operator, operators, setFieldValue]);

  const setDefaultRHSValue = function (operator, valueType = 0) {
    if (valueType !== 0) {
      setFieldValue(`rows[${index}].rhs`, "");
      return;
    }
    if (
      (operator === "string_not_equal" || operator === "string_equal") &&
      !Array.isArray(rhs)
    ) {
      setFieldValue(`rows[${index}].rhs`, []);
    } else if (
      Array.isArray(rhs) &&
      operator !== "string_not_equal" &&
      operator !== "string_equal"
    ) {
      setFieldValue(`rows[${index}].rhs`, "");
    }
  };

  const [firstTime, setFirstTime] = useState(true);

  return (
    <StyledCriteriaRow
      isHoveredCriteriaRow={isHoveredCriteriaRow}
      container
      item
      alignItems="center"
      alignContent="center"
    >
      {(index > 0 || !!lhs) && (
        <Grid item>
          <DeleteButton
            index={index}
            getEmptyRow={criteriaService.getEmptyRow}
          />
        </Grid>
      )}
      <Grid item xs={1} style={{ textAlign: "center" }}>
        {index === 0 ? (
          <b>Where</b>
        ) : (
          <SelectMultiLevel
            values={[{ name: "and" }, { name: "or" }]}
            valueKey="name"
            displayKey="name"
            label="condition"
            fullWidth
            onChange={(val) =>
              setFieldValue(`rows[${index}].condition`, val.name)
            }
            value={condition}
          />
        )}
      </Grid>

      <Grid item xs={3}>
        <SelectMultiLevel
          values={lookupColumns}
          valueKey="alias_path"
          displayKey="name"
          value={lhs}
          label="Column Name"
          fullWidth
          onChange={(val) => {
            let data = val;
            setLhsColumn(val);
            setFieldValue(`rows[${index}].lhs`, val.alias_path);
            setFieldValue(
              `rows[${index}].operatorType`,
              criteriaService.getOperatorType(val.alias_path, {data} )
            );
            // to auto scroll when new color is added and a new condition is applied for the first time
            if (index === 0 && firstTime) {
              setTimeout(() => {
                addFilterScroller?.current?.scrollIntoView({
                  behaviour: "smooth",
                  block: "end",
                });
              }, 100);
              setFirstTime(false);
            }
          }}
          forDuplication = {forDuplication}
        />
      </Grid>
      {!!lhs && (
        <>
          <Grid item xs={2}>
            <SelectMultiLevel
              values={operators}
              valueKey="symbol"
              displayKey="symbol"
              label="operator"
              fullWidth
              onChange={(val) => {
                setDefaultRHSValue(val.operator);
                setFieldValue(`rows[${index}].operator`, val.operator);
              }}
              value={operators.find((i) => i.operator === operator)}
            />
          </Grid>
          {!isRhsHidden && (
            <>
              <Grid item xs={2}>
                <SelectMultiLevel
                  values={rhsValueOptions}
                  valueKey="value"
                  displayKey="name"
                  label="value type"
                  onChange={(val) => {
                    if (val.value !== rhsType) {
                      setFieldValue(`rows[${index}].rhsType`, val.value);
                      setDefaultRHSValue(operator, val.value);
                    }
                  }}
                  value={rhsValueOptions.find((i) => i.value == rhsType)}
                />
              </Grid>
              { ((isDate && rhsType !== 0) || !isDate) && <Grid item xs={3}>
                <RhsHandler
                  lookupColumns={lookupColumns}
                  lookupListId={lookupListId}
                  currentSheetColumns={currentSheetColumns}
                  currentListId={currentListId}
                  type={rhsType}
                  lhsColumn={lhsColumn}
                  operator = {operator}
                  onChange={(val) => {
                    setFieldValue(`rows[${index}].rhs`, val);
                  }}
                  value={rhs}
                  fullWidth
                />
              </Grid>}
            </>
          )}
          {isDateRange && (
            <>
              <Grid item xs={2}>
                  <span>Start Date:</span>&nbsp;&nbsp;
                  <DateInput
                      value={startDate} 
                      isDateTime={false}
                      // format={dateFormat}
                      underLine={true}
                      styledPopper={true}
                      onChange={(val) => {
                        setFieldValue(`rows[${index}].startDate`, val);
                      }} />
              </Grid>
              <div style={{width:"28px"}} ></div>
              <Grid item xs={2}>
                  <span>End Date:</span>&nbsp;&nbsp;
                  <DateInput 
                      value={endDate} 
                      isDateTime={false}
                      // format={dateFormat}
                      underLine={true}
                      styledPopper={true}
                      onChange={(val) => {
                        setFieldValue(`rows[${index}].endDate`, val);
                      }} />
              </Grid>
            </>
          )}
            {
              isDate && !isDateRange && (rhsType === 0) && (
                <>
                  <div style={{width:"10px"}} ></div>
                  <Grid item xs={2} >
                    <div style={{"paddingTop":"5px"}} >
                    <DateInput 
                        value={isDate && rhs && Date.parse(rhs)} 
                        isDateTime={false}
                        // format={dateFormat}
                        underLine={true}
                        styledPopper={true}
                        onChange={(value)=>
                          setFieldValue(`rows.[${index}].rhs`,`${value.getFullYear()}-${value.getMonth() + 1}-${value.getDate()}`)
                        }
                      />
                    </div>
                  </Grid>
                </>
              )
            }
          {isNumberBetween && (
            <>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={numberFrom}
                  label="From"
                  onChange={(e) => {
                    setFieldValue(`rows[${index}].numberFrom`, e.target.value);
                  }}
                />
              </Grid>
              <StyledGrid item xs={2}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={numberTo}
                  label="To"
                  onChange={(e) => {
                    setFieldValue(`rows[${index}].numberTo`, e.target.value);
                  }}
                />
              </StyledGrid>
            </>
          )}
        </>
      )}
    </StyledCriteriaRow>
  );
}

function DeleteButton({ index, getEmptyRow }) {
  const formikContext = useFormikContext();
  const handleDelete = function () {
    let rows = [...formikContext.values.rows];

    rows.splice(index, 1);
    formikContext.setFieldValue("rows", rows);
  };

  return (
    <IconButton onClick={handleDelete}>
      <MdClose />
    </IconButton>
  );
}

function RhsHandler({
  type = 0,
  lookupColumns,
  lookupListId,
  currentSheetColumns,
  currentListId,
  onChange,
  value = "",
  lhsColumn,
  fullWidth,
  operator,
}) {
  let CMP;
  let extraProps = {};
  let options;
  if (lhsColumn.options && typeof lhsColumn.options == "string") {
    options = JSON.parse(lhsColumn.options); //updates columns also
  }
  else {
    options = lhsColumn.options;
  }
  const isStarColumn = options?.[0]?.is_star_column;
  if ((type === 0 && [3, 13, 11].includes(parseInt(lhsColumn.column_type_id))) || Array.isArray(value)) 
  {
    type = "VALUE_DROPDOWN";
  }
  if((parseInt(lhsColumn.column_type_id) === 1) && isStarColumn == true)
  {
    type = "VALUE_STAR";
  }
  switch (type) {
    case 1:
      CMP = SelectMultiLevel;
      let rhsColumns = currentSheetColumns;
      if (currentSheetColumns.length > 0) {
        let currentSheetColumnsTitle =
          currentSheetColumns[0]["list_name"] ||
          currentSheetColumns[0]["alias_path"].split(".")[0];
        rhsColumns = [
          ...[
            {
              name: currentSheetColumnsTitle,
              alias_path: currentSheetColumnsTitle,
              isTitledItem: true,
            },
          ],
          ...currentSheetColumns,
        ];
      }
      if (lookupColumns.length > 0) {
        let lookupColumnsTitle =
          lookupColumns[0]["list_name"] ||
          lookupColumns[0]["alias_path"].split(".")[0];
        rhsColumns = [
          ...rhsColumns,
          ...[
            {
              name: lookupColumnsTitle,
              alias_path: lookupColumnsTitle,
              isTitledItem: true,
            },
          ],
          ...lookupColumns,
        ];
      }
      
      extraProps = {
        values: rhsColumns,
        valueKey: "alias_path",
        displayKey: "name",
        label: "Column Name",
        onChange: (val) => onChange(val.alias_path),
      };
      break;
    case 2:
      CMP = SelectMultiLevel;
      extraProps = {
        values: loggedInUserColumns,
        valueKey: "alias_path",
        displayKey: "name",
        label: "User Column Name",
        onChange: (val) => onChange(val.alias_path),
      };
      break;
    case "VALUE_DROPDOWN":
      CMP = ValueDropdown;
      extraProps = {
        values: lhsColumn.options
          ? JSON.parse(lhsColumn.options)[0]["options"]
          : [],
        onInputChange: (e, newValue) => {
          if (!Array.isArray(value)) {
            onChange(newValue);
          }
        },
        onChange: (e, newValue) => {
          if (Array.isArray(value)) {
            onChange(newValue);
          }
        },
        onBlur: (e) => {
          if (!isEmpty(e.target.value) && Array.isArray(value)) {
            onChange([...value, e.target.value]);
          }
        },
      };
      break;
    case "VALUE_STAR":
      CMP = ValueStar;
      extraProps = {
        values: (operator == "string_equal" || operator == "string_not_equal")?[ 
          "is starred",
          "space",
          "yellow",
          "red",
          "orange",
          "green",
          "blue",
          "purple",
          "gray"
        ] : [ 
          "yellow",
          "red",
          "orange",
          "green",
          "blue",
          "purple",
          "gray"
        ],        
        onInputChange: (e, newValue) => {
          if (!Array.isArray(value)) {
            onChange(newValue);
          }
        },
        onChange: (e, newValue) => {
          if (Array.isArray(newValue)) {
            if (newValue.length === 1 && newValue[0] === 'is starred') {
              onChange(['is starred']);
            }else if (newValue.includes('is starred')) {
              window.appStore.notificationStore.showNotification(
                "You can either use is starred or other colors",
                "warning"
              );
            } else {
              onChange(newValue);
            }
          }
        },
        onBlur: (e) => {
          if (!isEmpty(e.target.value) && Array.isArray(value)) {
            onChange([...value, e.target.value]);
          }
        },
      };
      break;
    default:
      CMP = Value;
      extraProps = {
        onChange: (e) => onChange(e.target.value),
      };
  }
  return (
    <CMP
      onChange={onChange}
      value={value}
      {...extraProps}
      fullWidth={fullWidth}
    />
  );
}

function Value(props) {
  return <TextField fullWidth variant="outlined" {...props} />;
}

function ValueDropdown(props) {
  return (
    <StyledAutocomplete
      options={props.values ? props.values : []}
      multiple={Array.isArray(props.value)}
      disableCloseOnSelect={
        Array.isArray(props.value) && props.value.length > 0
      }
      freeSolo
      renderInput={(params) => <TextField {...params} variant="outlined" />}
      {...props}
      size="small"
    />
  );
}
function ValueStar(props) {

  const optionColors = {
    yellow: 'yellow',
    green: 'green',
    red: 'red',
    blue: 'blue',
    purple: 'purple',
    gray: 'gray',
    orange: 'orange'
  };

  return (
    <StyledAutocomplete
      options={props.values ? props.values : []}
      multiple={Array.isArray(props.value)}
      disableCloseOnSelect={Array.isArray(props.value) && props.value.length > 0}
      getOptionDisabled={(option) =>
        option == "space"
      }
      freeSolo
      renderInput={(params) => <TextField {...params} variant="outlined" />}
      renderOption={(option) => (
        option == "space" ?
        <div style={{margin: "3px solid #000"}}></div>
        :
        <div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <i
              id="star"
              className="fas fa-star"
              style={{ cursor: 'pointer', color: window.appStore.currentViewStore.getStarColumnHashValue(optionColors[option])?.color }}
            ></i>
            &nbsp;
            <span>{option}</span>
          </div>
        </div>
      )}   
      {...props}
      size="small"
    />
  );
}



const StyledAutocomplete = styled(Autocomplete)`
  && {
    .MuiOutlinedInput-root.MuiAutocomplete-inputRoot {
      padding-top: 2px;
      padding-bottom: 2px;
    }
  }
`;

const StyledCriteriaRow = styled(Grid)`
  ${(props) =>
    props.isHoveredCriteriaRow === true &&
    css`
      &:hover {
        background-color: #dddddd61;
      }
    `}
`;
const StyledGrid = styled(Grid)`
  && {
    margin-left: 15px;
  }
`;
export default React.memo(CriteriaRow);